/**
 * Fetches tool metadata for the active persona's MCP servers and builds the
 * lifecycle map used by the long-task machinery (ADR 059).
 *
 * When the MCP config's signature changes (`url + oauth_scopes` per server;
 * see `mcpConfigSignature`):
 *   - POST `mcp_config` to `POST /chat/tools`
 *   - read each tool's `_meta["corteva.com/longTask"]` block
 *   - validate groups
 *   - make two indexes:
 *     - `byToolName`: tool name -> binding, for the `useToolResultSweep` sweep.
 *       Keyed by both the advertised and the agent-facing tool name (see
 *       `configKeysByServerUrl`).
 *     - `byGroup`: `${server} ${group}` → group, for the poller and directive
 *       builder (resolves the status-tool name and submit `directiveTemplate`).
 *
 * Also mirrors the map into a module-level singleton (`moduleGroupMap`) so the
 * non-React polling loop in `useTaskPolling` can read it without prop
 * drilling. This map holds only static routing metadata; atoms/React Query are
 * the source of truth for task state.
 *
 * Components rendering off the map must read `longTaskGroupsAtom`, not the
 * mirror: see the note on `moduleGroupMap` below.
 */
import React from "react";
import { atom, useSetAtom } from "jotai";

import { fetchTools } from "services/cortevaChain/methods/fetchTools";
import {
  LONG_TASK_META_NAMESPACE,
  type LongTaskMeta,
  type LongTaskRole,
  type ToolDefinition,
} from "types/longTask";
import type { McpConfig } from "types/Mcp";
import {
  TERMINAL_STATUS_VALUES,
  type TerminalTaskStatus,
} from "types/TaskRegistry";

/** A fully-formed long-task group: exactly one tool per role on one server. */
export interface LongTaskGroup {
  server: string;
  group: string;
  submitTool: string;
  statusTool: string;
  fetchTool: string;
  directiveTemplate?: LongTaskMeta["directiveTemplate"];
}

/** Per-tool binding used by the sweep to route a tool result by role. */
export interface LongTaskToolBinding {
  server: string;
  group: string;
  role: LongTaskRole;
}

export interface LongTaskGroupMap {
  byToolName: Record<string, LongTaskToolBinding>;
  byGroup: Record<string, LongTaskGroup>;
}

const EMPTY_MAP: LongTaskGroupMap = { byToolName: {}, byGroup: {} };

/**
 * `(server, group)` composite key, space-separated. Unambiguous because neither
 * part can contain a space: `server` is a URL, and `group` is validated as a
 * non-empty `_meta` string that a space would have to be percent-escaped into to
 * reach a URL path. Not a hard guarantee — a server free-texting a group name
 * with a space in it would collide with a different `(server, group)` split — but
 * the only cost of a collision is one group's tools resolving to another's
 * lifecycle map, and both would have to come from the same trusted server.
 */
export function groupKey(server: string, group: string): string {
  return `${server} ${group}`;
}

export const longTaskGroupsAtom = atom<LongTaskGroupMap>(EMPTY_MAP);

/** How a settled `POST /chat/tools` load finished, and for which config. */
export interface LongTaskGroupsLoadState {
  /**
   * The `mcpConfigSignature` the settled map was built for. Storing the signature
   * rather than a bare "done" flag lets a consumer verify the map belongs to the
   * config it holds, because the atom resets to `null` on config change and
   * republishes only once the new load settles.
   */
  signature: string;
  /**
   * True when the request failed, which leaves the map empty. An empty map from a
   * failure is not authoritative evidence that a tool is unbound, so a consumer
   * routing individual tool results must keep deferring rather than act on it.
   */
  failed: boolean;
}

/**
 * How the tool-metadata load settled, or `null` while it is in flight. A failed
 * load publishes with `failed: true`, so if a consumer only needs the load to
 * have finished, it isn't blocked. Whether it finished and whether it
 * failed are the same value, so there is no window where one says the load
 * failed and the other still says it is running.
 */
export const longTaskGroupsLoadAtom = atom<LongTaskGroupsLoadState | null>(
  null
);

/**
 * Stable signature of an MCP config: each server's url + oauth_scopes, sorted.
 * Exported so consumers can compare against `longTaskGroupsLoadAtom` using the
 * same derivation as the loader.
 */
export function mcpConfigSignature(mcpConfig: McpConfig | undefined): string {
  return mcpConfig
    ? Object.values(mcpConfig)
        .map((s) => `${s.url}|${s.oauth_scopes ?? ""}`)
        .sort()
        .join(",")
    : "";
}

// Module-level mirror, kept in sync by useLongTaskGroups on each load. For
// readers that cannot subscribe to `longTaskGroupsAtom` (the poller and the pure
// builders, which are plain modules), or should not have to: reading the atom
// from `useToolResultSweep`'s effect would make the map a dep, and `TaskStatus`
// needs it only at click time.
//
// Never read this during render: a mutation here doesn't trigger re-render, so
// if a component first rendered before the load resolved, it shows the empty map
// until another update re-renders it (e.g. because props changed).
// Render paths read `longTaskGroupsAtom`, which the same loader writes.
export let moduleGroupMap: LongTaskGroupMap = EMPTY_MAP;

export function getLongTaskGroup(
  server: string,
  group: string
): LongTaskGroup | undefined {
  return moduleGroupMap.byGroup[groupKey(server, group)];
}

/**
 * Validates a `_meta` `directiveTemplate` block, keeping only terminal-status
 * keys with non-empty string values. The values are interpolated into the agent
 * directive at terminal time (`interpolateDirective` calls `.replace`), so a
 * non-string here (an arbitrary server `_meta` payload) would throw after the
 * terminal slot is claimed and permanently strand the directive. Returns
 * undefined when nothing valid remains, so the built-in default applies.
 */
function readDirectiveTemplate(
  value: unknown
): LongTaskMeta["directiveTemplate"] | undefined {
  if (!value || typeof value !== "object") return undefined;
  const src = value as Record<string, unknown>;
  const out: Partial<Record<TerminalTaskStatus, string>> = {};
  for (const key of TERMINAL_STATUS_VALUES) {
    const v = src[key];
    if (typeof v === "string" && v.length > 0) out[key] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function readLongTaskMeta(tool: ToolDefinition): LongTaskMeta | null {
  const meta = tool._meta;
  if (!meta || typeof meta !== "object") return null;
  // FastMCP injects a sibling `fastmcp` key, so key into the namespace rather than
  // treating `_meta` as single-key (ADR 059 / btsat-mcp ADR 010).
  const block = (meta as Record<string, unknown>)[LONG_TASK_META_NAMESPACE];
  if (!block || typeof block !== "object") return null;
  const b = block as Record<string, unknown>;
  if (b.version !== 1) return null;
  if (b.role !== "submit" && b.role !== "status" && b.role !== "fetch") {
    return null;
  }
  if (typeof b.group !== "string" || b.group.length === 0) return null;
  return {
    version: 1,
    role: b.role,
    group: b.group,
    directiveTemplate: readDirectiveTemplate(b.directiveTemplate),
  };
}

/**
 * Groups the `mcp_config` keys by server URL.
 *
 * A tool has two names: the advertised name its server uses in `tools/list`
 * (what `POST /chat/tools` returns) and the agent-facing name the API renames it
 * to, `${configKey}_${advertisedName}`, which is what every message's
 * `tool_name` carries. So these keys are the prefixes `byToolName` has to
 * recognize (ADR 059, "Amendment: the agent calls each tool by a prefixed name").
 *
 * Keyed by URL because `/chat/tools` reports the URL a tool was listed from, not
 * the key it was listed under. One URL can appear under several keys (one server
 * at different OAuth scopes), each a distinct prefix, so a URL maps to a list.
 */
function configKeysByServerUrl(
  mcpConfig: McpConfig | undefined
): Map<string, string[]> {
  const keysByUrl = new Map<string, string[]>();
  for (const [configKey, server] of Object.entries(mcpConfig ?? {})) {
    keysByUrl.set(server.url, [
      ...(keysByUrl.get(server.url) ?? []),
      configKey,
    ]);
  }
  return keysByUrl;
}

/**
 * Builds the lifecycle map from a flat tool list. A group is admitted only when
 * it has exactly one tool per role (submit/status/fetch) on the same server.
 * Partial or duplicate-role groups are dropped with a warning; their tools still
 * work as ordinary tools. Pure; exported for tests.
 *
 * `mcpConfig` supplies the name prefixes (see `configKeysByServerUrl`). Without
 * it the map holds advertised names only, which no message's tool name matches.
 */
export function buildLongTaskGroupMap(
  tools: ToolDefinition[],
  mcpConfig?: McpConfig
): LongTaskGroupMap {
  // Gather candidate (server, group) → role → tool name, detecting duplicates.
  const roleTools = new Map<
    string,
    {
      server: string;
      group: string;
      roles: Partial<Record<LongTaskRole, string>>;
      duplicate: boolean;
      directiveTemplate?: LongTaskMeta["directiveTemplate"];
    }
  >();

  for (const tool of tools) {
    const meta = readLongTaskMeta(tool);
    if (!meta) continue;
    const key = groupKey(tool.server, meta.group);
    const existing = roleTools.get(key) ?? {
      server: tool.server,
      group: meta.group,
      roles: {},
      duplicate: false,
    };
    if (existing.roles[meta.role]) {
      existing.duplicate = true;
    }
    existing.roles[meta.role] = tool.name;
    if (meta.role === "submit" && meta.directiveTemplate) {
      existing.directiveTemplate = meta.directiveTemplate;
    }
    roleTools.set(key, existing);
  }

  const map: LongTaskGroupMap = { byToolName: {}, byGroup: {} };
  const keysByUrl = configKeysByServerUrl(mcpConfig);
  // Agent-facing aliases, applied after every advertised name is in place so an
  // advertised name always wins a collision with a synthesized alias.
  const aliases: Array<{ name: string; binding: LongTaskToolBinding }> = [];

  for (const [key, candidate] of roleTools) {
    const { server, group, roles, duplicate, directiveTemplate } = candidate;
    if (duplicate || !roles.submit || !roles.status || !roles.fetch) {
      console.error(
        `Ignoring long-task group ${server}/${group}: ` +
          (duplicate
            ? "duplicate role"
            : `incomplete (have ${Object.keys(roles).join(", ") || "none"})`)
      );
      continue;
    }
    map.byGroup[key] = {
      server,
      group,
      submitTool: roles.submit,
      statusTool: roles.status,
      fetchTool: roles.fetch,
      directiveTemplate,
    };
    const bindTool = (toolName: string, role: LongTaskRole) => {
      const binding: LongTaskToolBinding = { server, group, role };
      map.byToolName[toolName] = binding;
      for (const configKey of keysByUrl.get(server) ?? []) {
        aliases.push({ name: `${configKey}_${toolName}`, binding });
      }
    };
    bindTool(roles.submit, "submit");
    bindTool(roles.status, "status");
    bindTool(roles.fetch, "fetch");
  }

  for (const { name, binding } of aliases) {
    if (!(name in map.byToolName)) map.byToolName[name] = binding;
  }
  return map;
}

/**
 * Loads the long-task lifecycle map for the given MCP config, re-fetching on
 * config change. Publishes the map to `longTaskGroupsAtom` and keeps the module
 * mirror in sync. On failure the map is left empty (long-task lifecycle disabled,
 * ordinary tools unaffected).
 *
 * Returns nothing: consumers read `longTaskGroupsAtom` (render paths) or the
 * module mirror (non-React readers), so a return value here would be a third way
 * to reach the same map. Writing through setters only also keeps this hook from
 * re-rendering its caller on every map change it does not itself use.
 */
export function useLongTaskGroups(
  mcpConfig: McpConfig | undefined,
  getAccessToken: () => Promise<string>
): void {
  const setMap = useSetAtom(longTaskGroupsAtom);
  const setLoadState = useSetAtom(longTaskGroupsLoadAtom);

  // Includes OAuth scopes so a persona that shares a server URL but differs in
  // scopes still triggers a reload. Prevents re-running on config object identity
  // changes that don't change the actual config.
  const configSignature = React.useMemo(
    () => mcpConfigSignature(mcpConfig),
    [mcpConfig]
  );

  // Read the render scope through one effect event rather than depending on it:
  // the signature is the intended trigger, and both values change identity for
  // reasons that must not re-run the fetch (`mcpConfig` per render,
  // `getAccessToken` on any MSAL account change). Returning them together keeps
  // the emptiness narrowing and the fetch call in one scope, so no assertion is
  // needed to hand a defined config to `fetchTools`.
  const readConfig = React.useEffectEvent(() => ({
    mcpConfig,
    getAccessToken,
  }));

  React.useEffect(() => {
    let cancelled = false;
    // Clear the load state so consumers don't read the previous config's map (or a
    // stale failure from it) while the new load is in flight.
    setLoadState(null);

    const { mcpConfig: config, getAccessToken: getToken } = readConfig();
    if (!config || Object.keys(config).length === 0) {
      moduleGroupMap = EMPTY_MAP;
      setMap(EMPTY_MAP);
      setLoadState({ signature: configSignature, failed: false });
      return;
    }
    (async () => {
      let failed = false;
      try {
        const tools = await fetchTools(config, getToken);
        if (cancelled) return;
        const next = buildLongTaskGroupMap(tools, config);
        moduleGroupMap = next;
        setMap(next);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load long-task tool metadata:", error);
        moduleGroupMap = EMPTY_MAP;
        setMap(EMPTY_MAP);
        failed = true;
      } finally {
        // Publish after the module mirror is updated so consumers that match the
        // signature can trust the map is current.
        if (!cancelled) {
          setLoadState({ signature: configSignature, failed });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [configSignature, setMap, setLoadState]);
}
